import React, { useState, useEffect, useCallback } from 'react';
import { X, Trash2, Download, Filter, Search, AlertTriangle, Database, User, Clock, Code } from 'lucide-react';
import errorTrackingService from '../../services/errorTrackingService';
import { supabase } from '../../lib/supabase';

const DebugPanel = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [errors, setErrors] = useState([]);
  const [selectedError, setSelectedError] = useState(null);
  const [filter, setFilter] = useState('all'); // all, rls, recent, critical
  const [searchQuery, setSearchQuery] = useState('');
  const [sessionInfo, setSessionInfo] = useState(null);
  const [stats, setStats] = useState(null);

  // Keyboard shortcut: Ctrl+Shift+D or Cmd+Shift+D
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e?.ctrlKey || e?.metaKey) && e?.shiftKey && e?.key === 'D') {
        e?.preventDefault();
        setIsOpen(prev => !prev);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Load errors and session info
  useEffect(() => {
    if (isOpen) {
      loadData();
      
      // Subscribe to error updates
      const unsubscribe = errorTrackingService?.subscribe(() => {
        loadData();
      });

      return unsubscribe;
    }
  }, [isOpen]);

  const loadData = async () => {
    // Load errors
    const allErrors = errorTrackingService?.getErrors();
    setErrors(allErrors);

    // Load statistics
    const statistics = errorTrackingService?.getStatistics();
    setStats(statistics);

    // Load session info
    const { data: { session } } = await supabase?.auth?.getSession();
    if (session) {
      setSessionInfo({
        userId: session?.user?.id,
        email: session?.user?.email,
        expiresAt: new Date(session.expires_at * 1000)?.toLocaleString(),
        accessToken: session?.access_token?.substring(0, 20) + '...',
        refreshToken: session?.refresh_token?.substring(0, 20) + '...',
        provider: session?.user?.app_metadata?.provider || 'email',
        createdAt: new Date(session.user.created_at)?.toLocaleString(),
        lastSignIn: new Date(session.user.last_sign_in_at)?.toLocaleString()
      });
    }
  };

  // Filter errors
  const filteredErrors = errors?.filter(error => {
    // Apply filter
    if (filter === 'rls' && !error?.isRLSError) return false;
    if (filter === 'critical' && error?.code !== '42P17' && error?.code !== '42501') return false;
    if (filter === 'recent') {
      const oneHourAgo = new Date(Date.now() - 60 * 60 * 1000);
      if (new Date(error.timestamp) < oneHourAgo) return false;
    }

    // Apply search
    if (searchQuery) {
      const query = searchQuery?.toLowerCase();
      return (error?.message?.toLowerCase()?.includes(query) ||
      error?.code?.toLowerCase()?.includes(query) ||
      error?.table?.toLowerCase()?.includes(query) ||
      error?.screen?.toLowerCase()?.includes(query) || error?.policyName?.toLowerCase()?.includes(query));
    }

    return true;
  });

  // Clear all logs
  const handleClearLogs = () => {
    if (window.confirm('Are you sure you want to clear all error logs?')) {
      errorTrackingService?.clearErrors();
      setSelectedError(null);
      loadData();
    }
  };

  // Export logs as JSON
  const handleExportLogs = () => {
    const dataStr = JSON.stringify(errors, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `supabase-errors-${new Date()?.toISOString()}.json`;
    link?.click();
    URL.revokeObjectURL(url);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[9999] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-7xl h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b bg-gray-50">
          <div>
            <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
              <Code className="w-5 h-5" />
              Supabase Debug Panel
            </h2>
            <p className="text-xs text-gray-600 mt-0.5">
              Press <kbd className="px-1.5 py-0.5 bg-gray-200 rounded text-xs font-mono">Ctrl+Shift+D</kbd> to toggle
            </p>
          </div>
          <button
            onClick={() => setIsOpen(false)}
            className="p-2 hover:bg-gray-200 rounded-lg transition-colors"
            aria-label="Close debug panel"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Stats Bar */}
        {stats && (
          <div className="grid grid-cols-5 gap-4 p-4 bg-gray-50 border-b text-sm">
            <div>
              <div className="text-gray-600 text-xs">Total Errors</div>
              <div className="font-bold text-lg">{stats?.total}</div>
            </div>
            <div>
              <div className="text-gray-600 text-xs">RLS Errors</div>
              <div className="font-bold text-lg text-red-600">{stats?.rlsErrors}</div>
            </div>
            <div>
              <div className="text-gray-600 text-xs">Last 24h</div>
              <div className="font-bold text-lg">{stats?.last24Hours}</div>
            </div>
            <div>
              <div className="text-gray-600 text-xs">Unique Tables</div>
              <div className="font-bold text-lg">{Object.keys(stats?.byTable)?.length}</div>
            </div>
            <div>
              <div className="text-gray-600 text-xs">Error Codes</div>
              <div className="font-bold text-lg">{Object.keys(stats?.byCode)?.length}</div>
            </div>
          </div>
        )}

        {/* Controls */}
        <div className="flex items-center gap-3 p-4 border-b bg-white">
          {/* Search */}
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search errors by message, code, table, screen..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e?.target?.value)}
              className="w-full pl-10 pr-4 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Filter */}
          <select
            value={filter}
            onChange={(e) => setFilter(e?.target?.value)}
            className="px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">All Errors</option>
            <option value="rls">RLS Only</option>
            <option value="critical">Critical</option>
            <option value="recent">Last Hour</option>
          </select>

          {/* Actions */}
          <button
            onClick={handleExportLogs}
            className="px-3 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors flex items-center gap-2"
          >
            <Download className="w-4 h-4" />
            Export
          </button>
          <button
            onClick={handleClearLogs}
            className="px-3 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 transition-colors flex items-center gap-2"
          >
            <Trash2 className="w-4 h-4" />
            Clear
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 flex overflow-hidden">
          {/* Error List */}
          <div className="w-1/2 border-r overflow-y-auto">
            {filteredErrors?.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-gray-500 p-8">
                <AlertTriangle className="w-12 h-12 mb-3 opacity-50" />
                <p className="text-sm">No errors found</p>
              </div>
            ) : (
              <div className="divide-y">
                {filteredErrors?.map((error) => (
                  <button
                    key={error?.id}
                    onClick={() => setSelectedError(error)}
                    className={`w-full text-left p-4 hover:bg-gray-50 transition-colors ${
                      selectedError?.id === error?.id ? 'bg-blue-50 border-l-4 border-blue-600' : ''
                    }`}
                  >
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <div className="flex items-center gap-2">
                        <span className={`px-2 py-0.5 rounded text-xs font-mono ${
                          error?.isRLSError ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-700'
                        }`}>
                          {error?.code}
                        </span>
                        {error?.isRLSError && (
                          <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded">RLS</span>
                        )}
                      </div>
                      <span className="text-xs text-gray-500">
                        {new Date(error.timestamp)?.toLocaleTimeString()}
                      </span>
                    </div>
                    <p className="text-sm font-medium text-gray-900 mb-1 line-clamp-2">
                      {error?.message}
                    </p>
                    <div className="flex items-center gap-3 text-xs text-gray-600">
                      <span className="font-mono">{error?.table}</span>
                      <span>•</span>
                      <span>{error?.operation}</span>
                      <span>•</span>
                      <span>{error?.screen}</span>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Error Details */}
          <div className="w-1/2 overflow-y-auto bg-gray-50">
            {selectedError ? (
              <div className="p-6 space-y-6">
                {/* Error Info */}
                <div>
                  <h3 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                    <AlertTriangle className="w-5 h-5 text-red-600" />
                    Error Details
                  </h3>
                  <div className="bg-white rounded-lg p-4 space-y-3 text-sm">
                    <div>
                      <span className="text-gray-600">Code:</span>
                      <span className="ml-2 font-mono font-bold">{selectedError?.code}</span>
                    </div>
                    <div>
                      <span className="text-gray-600">Message:</span>
                      <p className="mt-1 text-gray-900">{selectedError?.message}</p>
                    </div>
                    {selectedError?.details && (
                      <div>
                        <span className="text-gray-600">Details:</span>
                        <p className="mt-1 text-gray-900">{selectedError?.details}</p>
                      </div>
                    )}
                    {selectedError?.hint && (
                      <div>
                        <span className="text-gray-600">Hint:</span>
                        <p className="mt-1 text-gray-900">{selectedError?.hint}</p>
                      </div>
                    )}
                    <div>
                      <span className="text-gray-600">Timestamp:</span>
                      <span className="ml-2">{new Date(selectedError.timestamp)?.toLocaleString()}</span>
                    </div>
                  </div>
                </div>

                {/* Query Info */}
                <div>
                  <h3 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                    <Database className="w-5 h-5 text-blue-600" />
                    Query Details
                  </h3>
                  <div className="bg-white rounded-lg p-4 space-y-3 text-sm">
                    <div>
                      <span className="text-gray-600">Table:</span>
                      <span className="ml-2 font-mono">{selectedError?.table}</span>
                    </div>
                    <div>
                      <span className="text-gray-600">Operation:</span>
                      <span className="ml-2 font-mono">{selectedError?.operation}</span>
                    </div>
                    <div>
                      <span className="text-gray-600">Screen:</span>
                      <span className="ml-2">{selectedError?.screen}</span>
                    </div>
                    <div>
                      <span className="text-gray-600">Action:</span>
                      <span className="ml-2">{selectedError?.action}</span>
                    </div>
                    {selectedError?.policyName && (
                      <div>
                        <span className="text-gray-600">RLS Policy:</span>
                        <span className="ml-2 font-mono text-red-600">{selectedError?.policyName}</span>
                      </div>
                    )}
                    {selectedError?.executionTime && (
                      <div>
                        <span className="text-gray-600">Execution Time:</span>
                        <span className="ml-2">{selectedError?.executionTime}ms</span>
                      </div>
                    )}
                    {Object.keys(selectedError?.queryParams || {})?.length > 0 && (
                      <div>
                        <span className="text-gray-600">Parameters:</span>
                        <pre className="mt-1 bg-gray-100 p-2 rounded text-xs font-mono overflow-x-auto">
                          {JSON.stringify(selectedError?.queryParams, null, 2)}
                        </pre>
                      </div>
                    )}
                    {Object.keys(selectedError?.filters || {})?.length > 0 && (
                      <div>
                        <span className="text-gray-600">Filters:</span>
                        <pre className="mt-1 bg-gray-100 p-2 rounded text-xs font-mono overflow-x-auto">
                          {JSON.stringify(selectedError?.filters, null, 2)}
                        </pre>
                      </div>
                    )}
                  </div>
                </div>

                {/* Session Info */}
                {sessionInfo && (
                  <div>
                    <h3 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                      <User className="w-5 h-5 text-green-600" />
                      User Session
                    </h3>
                    <div className="bg-white rounded-lg p-4 space-y-3 text-sm">
                      <div>
                        <span className="text-gray-600">User ID:</span>
                        <span className="ml-2 font-mono text-xs">{sessionInfo?.userId}</span>
                      </div>
                      <div>
                        <span className="text-gray-600">Email:</span>
                        <span className="ml-2">{sessionInfo?.email}</span>
                      </div>
                      <div>
                        <span className="text-gray-600">Provider:</span>
                        <span className="ml-2">{sessionInfo?.provider}</span>
                      </div>
                      <div>
                        <span className="text-gray-600">Session Expires:</span>
                        <span className="ml-2">{sessionInfo?.expiresAt}</span>
                      </div>
                      <div>
                        <span className="text-gray-600">Last Sign In:</span>
                        <span className="ml-2">{sessionInfo?.lastSignIn}</span>
                      </div>
                    </div>
                  </div>
                )}

                {/* Request/Response */}
                {selectedError?.statusCode && (
                  <div>
                    <h3 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                      <Clock className="w-5 h-5 text-purple-600" />
                      HTTP Details
                    </h3>
                    <div className="bg-white rounded-lg p-4 space-y-3 text-sm">
                      <div>
                        <span className="text-gray-600">Status Code:</span>
                        <span className="ml-2 font-mono">{selectedError?.statusCode}</span>
                      </div>
                      {Object.keys(selectedError?.requestHeaders || {})?.length > 0 && (
                        <div>
                          <span className="text-gray-600">Request Headers:</span>
                          <pre className="mt-1 bg-gray-100 p-2 rounded text-xs font-mono overflow-x-auto max-h-32">
                            {JSON.stringify(selectedError?.requestHeaders, null, 2)}
                          </pre>
                        </div>
                      )}
                      {Object.keys(selectedError?.responseHeaders || {})?.length > 0 && (
                        <div>
                          <span className="text-gray-600">Response Headers:</span>
                          <pre className="mt-1 bg-gray-100 p-2 rounded text-xs font-mono overflow-x-auto max-h-32">
                            {JSON.stringify(selectedError?.responseHeaders, null, 2)}
                          </pre>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Stack Trace */}
                {selectedError?.stackTrace && (
                  <div>
                    <h3 className="font-bold text-gray-900 mb-3">Stack Trace</h3>
                    <pre className="bg-white rounded-lg p-4 text-xs font-mono overflow-x-auto max-h-64">
                      {selectedError?.stackTrace}
                    </pre>
                  </div>
                )}
              </div>
            ) : (
              <div className="flex items-center justify-center h-full text-gray-500">
                <p className="text-sm">Select an error to view details</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DebugPanel;